import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle,
  ArrowRight,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  Video,
} from "lucide-react";

const CallEntryPage = () => {
  const { shortId } = useParams();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Checking your consultation link.");
  const [isRetrying, setIsRetrying] = useState(false);

  const whatsappNumber = "2348069804062";

  const whatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Zaviramed, I am trying to join my consultation call but the link is not working. My call code is: ${shortId || "not available"}`
    );
  }, [shortId]);

  const emailHref = useMemo(() => {
    const subject = encodeURIComponent("Consultation Call Link Support");
    const body = encodeURIComponent(
      `Hello Zaviramed,\n\nI am trying to join my consultation call but the link is not working.\n\nCall code: ${shortId || "not available"}\n\nPlease assist me.`
    );

    return `mailto:support@zaviramed.com.ng?subject=${subject}&body=${body}`;
  }, [shortId]);

  const openWhatsappSupport = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${whatsappText}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openEmailSupport = () => {
    window.location.href = emailHref;
  };

  const validateMeeting = async ({ retry = false } = {}) => {
    try {
      if (retry) {
        setIsRetrying(true);
      }

      setStatus("loading");
      setMessage("Checking your consultation link.");

      if (!shortId) {
        setStatus("error");
        setMessage("This consultation link is incomplete or invalid.");
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      if (!backendUrl) {
        setStatus("error");
        setMessage(
          "The consultation service is not configured correctly. Please contact Zaviramed support."
        );
        return;
      }

      const res = await axios.get(
        `${backendUrl}/api/meeting/validate/${shortId}`,
        {
          timeout: 30000,
        }
      );

      if (res.data?.valid && res.data?.link) {
        setStatus("redirecting");
        setMessage("Consultation confirmed. Redirecting you to the call room.");

        setTimeout(() => {
          window.location.href = res.data.link;
        }, 700);

        return;
      }

      setStatus("error");
      setMessage(
        res.data?.message ||
          "This consultation link is no longer valid or has expired."
      );
    } catch (err) {
      console.error("Meeting validation error:", err);

      setStatus("error");

      if (err.code === "ECONNABORTED") {
        setMessage(
          "The request took too long. Please check your internet connection and try again."
        );
        return;
      }

      setMessage(
        err.response?.data?.message ||
          "This consultation link is no longer valid or could not be verified."
      );
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    validateMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortId]);

  const isLoading = status === "loading" || status === "redirecting";

  return (
    <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
      <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
          <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div>
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-12 w-auto object-contain"
              />

              <div className="mt-16">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                  <ShieldCheck size={15} />
                  Secure call entry
                </div>

                <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                  Private consultation access for your doctor call.
                </h1>

                <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                  We check your call link before opening the consultation room, so
                  only valid sessions can continue.
                </p>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-slate-300">
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Service</span>
                <span className="font-medium text-white">Video consultation</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Provider</span>
                <span className="font-medium text-white">Zaviramed</span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Access</span>
                <span className="font-medium text-white">Private link</span>
              </div>
            </div>
          </aside>

          <section className="p-5 sm:p-8 md:p-10 lg:p-12">
            <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-10 w-auto object-contain"
              />

              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <ShieldCheck size={14} />
                Secure
              </div>
            </div>

            <div
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                isLoading
                  ? "border-slate-200 bg-slate-50 text-slate-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }`}
            >
              {isLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <AlertCircle size={14} />
              )}

              {status === "redirecting"
                ? "Redirecting"
                : isLoading
                ? "Validating link"
                : "Session unavailable"}
            </div>

            <div className="mt-8 max-w-2xl">
              <div
                className={`mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border ${
                  isLoading
                    ? "border-slate-200 bg-slate-50 text-slate-700"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                {isLoading ? (
                  status === "redirecting" ? (
                    <Video size={32} />
                  ) : (
                    <Loader2 size={32} className="animate-spin" />
                  )
                ) : (
                  <AlertCircle size={32} />
                )}
              </div>

              <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                {status === "redirecting"
                  ? "Opening your consultation room"
                  : isLoading
                  ? "Connecting you to your doctor"
                  : "This consultation session is unavailable"}
              </h2>

              <p className="mt-4 text-base leading-7 text-slate-600">
                {status === "redirecting"
                  ? "Your link is valid. You will be redirected to the secure call room shortly."
                  : isLoading
                  ? "Please wait while we confirm that this consultation link is active and safe to open."
                  : "The link may have expired, been closed by the doctor, or may not be valid for this session."}
              </p>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm leading-6 text-slate-700">{message}</p>
              </div>
            </div>

            <div className="mt-8 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Session details
                </p>
              </div>

              <div className="grid gap-0 sm:grid-cols-2">
                <div className="border-b border-slate-200 px-4 py-4 sm:border-b-0 sm:border-r">
                  <p className="text-xs font-medium text-slate-500">
                    Session type
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">
                    Doctor consultation call
                  </p>
                </div>

                <div className="px-4 py-4">
                  <p className="text-xs font-medium text-slate-500">
                    Call code
                  </p>
                  <p className="mt-1 truncate text-sm font-semibold text-slate-950">
                    {shortId || "Not available"}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {isLoading ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-slate-200 px-5 py-4 text-sm font-semibold text-slate-500 sm:w-auto"
                >
                  <Loader2 size={18} className="animate-spin" />
                  Checking link
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => validateMeeting({ retry: true })}
                    disabled={isRetrying}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-4 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                  >
                    {isRetrying ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <RefreshCw size={18} />
                    )}
                    Retry link
                  </button>

                  <button
                    type="button"
                    onClick={openEmailSupport}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-50 active:scale-[0.99] sm:w-auto"
                  >
                    <Mail size={18} />
                     support
                  </button>

                
                </>
              )}
            </div>

            <div className="mt-10 grid gap-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  Keep this page open while your consultation link is being
                  checked.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  If the session is unavailable, contact Zaviramed support with
                  your call code.
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default CallEntryPage;